//
// README - Beta Component
//
// This component is marked "beta" and will go through some iterations
// before landing at a final solution. Please reach out to the Cadence team
// if you intend to use this in the meantime and we'll help you out!
//

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import classNames from 'classnames';

interface RCListingGridProps {
  children: React.ReactNode;
  loading?: boolean;
  sidebar?: SidebarProps;
}

export function RCListingGrid({
  loading = false,
  sidebar = undefined,
  children,
}: RCListingGridProps) {
  return (
    <div className={classNames(
      'rc-listing-grid',
      { 'rc-listing-grid--loading': loading },
      { 'rc-listing-grid--with-sidebar': sidebar },
    )}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      {sidebar && <ListingGridSidebar {...sidebar} />}
      <ol className="rc-listing-grid__content">
        {children}
      </ol>
    </div>
  );
}

interface SidebarProps {
  content: React.ReactNode;
  isVisible?: boolean;
}

function ListingGridSidebar({
  isVisible = true,
  content,
}: SidebarProps) {
  const animationDistance = 'calc(-1 * var(--sidebar-with-gutter))';
  return (
    <AnimatePresence initial={false}>
      {isVisible &&
        <motion.div
          className="rc-listing-grid__sidebar"
          transition={{
            x: { type: 'spring', stiffness: 300, damping: 30 },
            opacity: { duration: 0.2 },
          }}
          initial={{ opacity: 0, marginLeft: animationDistance }}
          animate={{ opacity: 1, marginLeft: 0 }}
          exit={{ opacity: 0, marginLeft: animationDistance }}
        >
          <div className="rc-listing-grid__sidebar__content">
            {content}
          </div>
        </motion.div>
      }
    </AnimatePresence>
  );
}

interface GridItemProps {
  colSpan?: 'one' | 'all',
  children: React.ReactNode;
}

function ListingGridItem({
  colSpan = 'one',
  children,
}: GridItemProps) {
  return (
    <li className={classNames(
      'rc-listing-grid__item',
      { 'rc-listing-grid__item--span-all': colSpan === 'all' },
    )}>
      {children}
    </li>
  );
}

RCListingGrid.Item = ListingGridItem;
